import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublicBadge } from "../features/badge/api";
import type { PublicBadge } from "../features/badge/api";
import { formatDateTime } from "../features/dashboard/format";
import { QrBadge } from "../components/QrBadge";
import { ApiError } from "../lib/api-client";
import { useCompanyBranding } from "../lib/useCompanyBranding";
import "../App.css";

export function PublicBadgePage() {
  const { badgeCode = "" } = useParams<{ badgeCode: string }>();
  const [badge, setBadge] = useState<PublicBadge | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { logoUrl } = useCompanyBranding(badge?.tenant_company_code);

  useEffect(() => {
    if (!badgeCode) return;

    let cancelled = false;

    getPublicBadge(badgeCode)
      .then((result) => {
        if (cancelled) return;
        setBadge(result);
        setLoading(false);
      })
      .catch((requestError: unknown) => {
        if (cancelled) return;
        setError(
          requestError instanceof ApiError && requestError.status === 404
            ? "This badge link isn't valid, or the visit it belonged to no longer exists."
            : requestError instanceof Error
              ? requestError.message
              : "Couldn't load this badge.",
        );
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [badgeCode]);

  return (
    <main className="invite-shell">
      <section className="hero">
        <p className="eyebrow">
          {logoUrl ? (
            <img src={logoUrl} alt={badge?.tenant_company_code ?? "Company logo"} className="company-logo" />
          ) : (
            <span className="brand-wordmark">
              0todeploy <span className="brand-vms">VMS</span>
            </span>
          )}
          <span className="role-chip">Visitor Badge</span>
        </p>
        <h1>Your entry pass</h1>
        <p className="subtitle">Show this QR code at the gate for check-in.</p>
      </section>

      <section className="panel">
        {!badgeCode ? <p className="error-banner">No badge code provided.</p> : null}
        {badgeCode && loading ? <p>Loading your badge...</p> : null}
        {badgeCode && error ? <p className="error-banner">{error}</p> : null}
        {badgeCode && !loading && !error && badge ? (
          <div className="approval-context">
            <QrBadge badgeCode={badge.badge_code} template={badge.template} />
            <div className="context-grid">
              <div className="context-item">
                <p className="context-label">Visitor</p>
                <p>{badge.visitor_name}</p>
              </div>
              <div className="context-item">
                <p className="context-label">Host</p>
                <p>{badge.host_name}</p>
              </div>
              {badge.company ? (
                <div className="context-item">
                  <p className="context-label">Company</p>
                  <p>{badge.company}</p>
                </div>
              ) : null}
              <div className="context-item">
                <p className="context-label">Purpose</p>
                <p>{badge.purpose}</p>
              </div>
              <div className="context-item">
                <p className="context-label">Status</p>
                <p>{badge.status}</p>
              </div>
              {badge.expected_exit ? (
                <div className="context-item">
                  <p className="context-label">Expected Exit</p>
                  <p>{formatDateTime(badge.expected_exit)}</p>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>
    </main>
  );
}
